<template>
  <div
    class="line-item-edit-grid"
    @keypress.enter="onSubmit"
    @keyup.esc="onCancel"
  >
    <div class="row">
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :errors="v$.description.$errors"
        :input-id="`description-${index}`"
        :label="t('common.description')"
        class="w-3"
      >
        <ProductQuickSearch
          :id="inputId"
          v-model="lineItem"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled"
        />
      </InputWrapper>

      <UnitOfMeasureInputGroup
        :id="`handling-${index}`"
        v-model="v$.quantity.$model"
        :disabled="disabled"
        :units="[]"
        :errors="v$.quantity.$errors"
        :uom-errors="v$.handlingUnit.$errors"
        :label="t('lineItems.handling')"
        class="w-3"
      >
        <template #uom>
          <RygenDropdown
            :id="`handling-${index}-unit`"
            v-model="v$.handlingUnit.$model"
            :disabled="disabled"
            :options="handlingUnits.data"
            data-key="id"
            option-label="name"
            :loading="handlingUnits.loading"
            filter
            :placeholder="t('uoms.unit')"
          />
        </template>
      </UnitOfMeasureInputGroup>

      <UnitOfMeasureInputGroup
        :id="`weight-${index}`"
        v-model="v$.weight.$model"
        v-model:uom="v$.weightUom.$model"
        :disabled="disabled"
        :units="WEIGHT_UNITS"
        :errors="v$.weight.$errors"
        :uom-errors="v$.weightUom.$errors"
        :label="t('lineItems.weight')"
        class="w-3"
      />

      <DimensionsInputGroup
        :id="`dims-${index}`"
        v-model:length="v$.lengthDim.$model"
        v-model:width="v$.widthDim.$model"
        v-model:height="v$.heightDim.$model"
        v-model:uom="v$.dimensionalUom.$model"
        :disabled="disabled"
        :length-errors="v$.lengthDim.$errors"
        :width-errors="v$.widthDim.$errors"
        :height-errors="v$.heightDim.$errors"
        :uom-errors="v$.dimensionalUom.$errors"
        :units="availableDimsUoms"
        class="w-6"
        optional
      />
    </div>

    <div class="row">
      <UnitOfMeasureInputGroup
        :id="`packaging-quantity-${index}`"
        v-model="lineItem.packagingQty"
        v-model:uom="lineItem.packagingUnit"
        :disabled="disabled"
        :units="[]"
        :errors="v$.packagingQty.$errors"
        :uom-errors="v$.packagingUnit.$errors"
        :label="t('lineItems.packaging')"
        class="w-3"
        optional
      >
        <template #uom>
          <RygenDropdown
            :id="`packaging-quantity-${index}-unit`"
            v-model="v$.packagingUnit.$model"
            :disabled="disabled"
            :options="handlingUnits.data"
            data-key="id"
            option-label="name"
            :loading="handlingUnits.loading"
            filter
            :placeholder="t('uoms.unit')"
          />
        </template>
      </UnitOfMeasureInputGroup>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`nmfc-number-${index}`"
        :label="t('lineItems.nmfcNumber')"
        :errors="v$.nmfcNumber.$errors"
        class="w-3"
        help-text="12345-1"
        optional
      >
        <ClearableInputText
          :id="inputId"
          v-model="v$.nmfcNumber.$model"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled"
          :label="t('lineItems.nmfcNumber')"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`freight-class-${index}`"
        :label="t('lineItems.class')"
        :errors="v$.freightClass.$errors"
        class="w-3"
        optional
      >
        <RygenDropdown
          :id="inputId"
          v-model="lineItem.freightClass"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled"
          :options="freightClasses"
          show-clear
          :placeholder="t('lineItems.class')"
        />
        <RygenButton
          v-if="showSuggestedFreightClass"
          class="suggestion-button"
          mode="link"
          :disabled="disabled"
          :label="
            t('common.useSuggestedParam', {
              param: suggestedFreightClass,
            })
          "
          @click="onUseSuggestedFreightClassClicked"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :errors="v$.linear.$errors"
        :input-id="`linear-feet-${index}`"
        :label="t('common.units.linearFeet', 2)"
        class="w-3"
        optional
      >
        <InputNumber
          :id="inputId"
          v-model="v$.linear.$model"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled"
          :min="0"
          :placeholder="t('common.units.linearFeet', 2)"
        />
      </InputWrapper>
    </div>

    <div class="row">
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :errors="v$.productNumber.$errors"
        :input-id="`product-number-${index}`"
        :label="t('lineItems.productNumber')"
        class="w-3"
        optional
      >
        <InputText
          :id="inputId"
          v-model="v$.productNumber.$model"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled && !$hasAuthority('referenceData', 'update')"
          :placeholder="t('lineItems.productNumber')"
        />
      </InputWrapper>

      <UnitOfMeasureInputGroup
        :id="`density-${index}`"
        v-model="lineItem.density"
        v-model:uom="lineItem.densityUom"
        :disabled="disabled"
        :units="DENSITY_UNITS"
        :label="t('lineItems.density')"
        :max-fraction-digits="1"
        class="w-3"
        optional
      />

      <InputWrapper
        v-slot="{ ariaProps, inputId }"
        :input-id="`value-of-goods-${index}`"
        :label="t('common.paramValues')"
        class="w-3"
        optional
      >
        <InputMoney
          :id="inputId"
          v-model="lineItem.valueOfGoods"
          v-bind="ariaProps"
          :disabled="disabled"
          :placeholder="t('common.paramValues')"
        />
      </InputWrapper>
    </div>

    <div class="row">
      <WrappedSwitch
        :id="`turnable-${index}`"
        v-model="lineItem.turnable"
        :label="t('lineItems.turnable')"
        class="w-2"
        :disabled="disabled"
      />

      <WrappedSwitch
        :id="`stackable-${index}`"
        v-model="lineItem.stackable"
        :label="t('lineItems.stackable')"
        class="w-2"
        :disabled="disabled"
      />

      <WrappedSwitch
        :id="`hazmat-${index}`"
        v-model="lineItem.hazmatData.hazmat"
        :label="t('lineItems.hazardous')"
        class="w-2"
        :disabled="disabled"
      />

      <WrappedSwitch
        :id="`refrigerated-${index}`"
        v-model="lineItem.temperatureSettings.needsRefrigeration"
        :label="t('temperatureSettings.refrigerated')"
        :disabled="disabled"
        class="w-2"
      />

      <WrappedSwitch
        v-if="showSaveProductSwitch"
        :id="`save-product-${index}`"
        v-model="lineItem.saveToProductList"
        :label="
          lineItem.productId
            ? t('lineItems.updateProductList')
            : t('lineItems.saveToProductList')
        "
        class="w-2"
        :disabled="disabled"
      />
    </div>

    <div v-if="isHazardous" class="row" :class="{ 'border-top': !showButtons }">
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`un-number-${index}`"
        :label="t('lineItems.unOrNaNumber')"
        :errors="v$.hazmatData.unNumber.$errors"
        class="w-2"
        help-text="UN1234 / NA1234"
      >
        <RygenInputMask
          :id="inputId"
          v-model="v$.hazmatData.unNumber.$model"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled"
          mask="aa9999"
          :placeholder="t('lineItems.unOrNaNumber')"
          @complete="onUpdateUnNumber"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`hazard-description-${index}`"
        :disabled="disabled"
        :label="t('lineItems.hazardDescription')"
        :errors="v$.hazmatData.hazardDescription.$errors"
        class="w-4"
      >
        <InputText
          :id="inputId"
          v-model="v$.hazmatData.hazardDescription.$model"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled"
          :placeholder="t('lineItems.hazardDescription')"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`hazard-class-${index}`"
        :label="t('lineItems.hazardClass')"
        :errors="v$.hazmatData.hazardClass.$errors"
        class="w-3"
      >
        <RygenDropdown
          :id="inputId"
          v-model="v$.hazmatData.hazardClass.$model"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled"
          :options="HAZARD_CLASSES"
          append-to="body"
          option-label="name"
          option-value="code"
          :placeholder="t('lineItems.hazardClass')"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`packing-group-${index}`"
        :label="t('lineItems.packingGroup')"
        :errors="v$.hazmatData.packingGroup.$errors"
        class="w-3"
      >
        <RygenDropdown
          :id="inputId"
          v-model="v$.hazmatData.packingGroup.$model"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled"
          :options="packingGroupOptions"
          append-to="body"
          option-label="label"
          option-value="value"
          :placeholder="t('lineItems.packingGroup')"
        />
      </InputWrapper>
    </div>

    <div
      v-if="needsRefrigeration"
      class="row"
      :class="{ 'border-top': !showButtons }"
    >
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`min-temp-${index}`"
        :errors="v$.temperatureSettings.minTemp.$errors"
        :label="t('temperatureSettings.minTemp')"
        class="w-2"
      >
        <InputNumber
          :id="inputId"
          v-model="v$.temperatureSettings.minTemp.$model"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled"
          :suffix="temperatureSuffix"
          :max-fraction-digits="1"
          :placeholder="t('temperatureSettings.minTemp')"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`max-temp-${index}`"
        :errors="v$.temperatureSettings.maxTemp.$errors"
        :label="t('temperatureSettings.maxTemp')"
        class="w-2"
      >
        <InputNumber
          :id="inputId"
          v-model="v$.temperatureSettings.maxTemp.$model"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled"
          :suffix="temperatureSuffix"
          :max-fraction-digits="1"
          :placeholder="t('temperatureSettings.maxTemp')"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`temperature-unit-${index}`"
        :errors="v$.temperatureSettings.temperatureUom.$errors"
        :label="t('temperatureSettings.tempUnit')"
        class="w-2"
      >
        <RygenDropdown
          :id="inputId"
          v-model="v$.temperatureSettings.temperatureUom.$model"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :disabled="disabled"
          option-label="label"
          option-value="value"
          :options="temperatureUomOptions"
          :placeholder="t('temperatureSettings.tempUnit')"
        />
      </InputWrapper>
    </div>

    <LineItemStopsRow
      v-if="showStops && ShipmentLineItem.isShipmentLineItem(lineItem)"
      v-model="lineItem"
      class="row"
      :class="{ 'border-top': isHazardous || needsRefrigeration }"
      :disabled="disabled"
      :index="index"
    />

    <div
      v-if="lineItemReferenceNumberTypes.data?.length"
      class="row border-top"
    >
      <ReferenceNumbersEdit
        v-model="lineItem.referenceNumbers"
        :disabled="disabled"
        :levels="[ReferenceNumberTypeLevel.LINE_ITEM]"
        class="w-8"
      />
    </div>

    <div
      v-if="showButtons"
      class="flex flex-gap-1 justify-content-end border-top"
    >
      <RygenButton
        v-if="lineItem.id"
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        v-if="removable"
        mode="secondary"
        :disabled="disabled"
        :icon="PrimeIcons.TRASH"
        :label="t('common.remove')"
        :severity="Severity.DANGER"
        @click="emit('remove', lineItem)"
      />
      <RygenButton
        mode="primary"
        :disabled="disabled"
        :label="t('common.done')"
        @click="onSubmit"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import {
  computed,
  inject,
  nextTick,
  onMounted,
  ref,
  toRef,
  toRefs,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import {
  matches,
  maxValue,
  minValue,
  required,
  requiredIf,
  useUid,
  useUnsavedChangesConfirmation,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { mapEnumToTranslatedDropdownValues } from '@rygen/rygen-ui/i18n';
import {
  DENSITY_UNITS,
  DIMS_UNITS,
  DimsUom,
  ErrorResponse,
  freightClasses,
  HandlingUnit,
  HazardClass,
  Loadable,
  Optional,
  PackingGroup,
  ReferenceNumberTypeLevel,
  Severity,
  TEMPERATURE_UNITS,
  WEIGHT_UNITS,
} from '@rygen/rygen-ui/model';

import {
  useLineItemMeasurements,
  useReferenceNumberTypes,
} from '@/composables';
import { LineItem, ShipmentLineItem } from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import ClearableInputText from '@/components/form/ClearableInputText.vue';
import LineItemStopsRow from '@/components/lineItem/LineItemStopsRow.vue';
import ProductQuickSearch from '@/components/lineItem/ProductQuickSearch.vue';
import ReferenceNumbersEdit from '@/components/referenceNumber/ReferenceNumbersEdit.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import DimensionsInputGroup from '@rygen/rygen-ui/components/form/DimensionsInputGroup.vue';
import InputMoney from '@rygen/rygen-ui/components/form/InputMoney.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import RygenInputMask from '@rygen/rygen-ui/components/form/RygenInputMask.vue';
import UnitOfMeasureInputGroup from '@rygen/rygen-ui/components/form/UnitOfMeasureInputGroup.vue';
import WrappedSwitch from '@rygen/rygen-ui/components/form/WrappedSwitch.vue';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';

const HAZARD_CLASSES = [...HazardClass.ALL_CLASSES];

const packingGroupOptions = mapEnumToTranslatedDropdownValues(
  PackingGroup,
  'PackingGroup',
);

const props = defineProps({
  modelValue: {
    type: LineItem,
    default: () => new LineItem(),
  },
  index: {
    type: Number,
    default: () => 1,
  },
  handlingUnits: {
    type: Object as PropType<Loadable<HandlingUnit[]>>,
    default: () => ({ loading: false }),
  },
  errorResponse: {
    type: Object as PropType<ErrorResponse>,
    default: () => undefined,
  },
  removable: {
    type: Boolean,
    default: () => false,
  },
  showStops: {
    type: Boolean,
    default: () => false,
  },
  /**
   * Determines if `update:model-value` is only sent on user confirmation
   *
   * When this prop is true, we render confirmation buttons and short-circuit
   * two-way binding. `update:model-value` will **not** be emitted on changes.
   * It will only be emitted when the _Done_ button is pressed.
   */
  requiresConfirmation: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  disableDefaultReferenceNumbers: {
    type: Boolean,
    default: () => false,
  },
  showSaveProductSwitch: {
    type: Boolean,
    default: () => false,
  },
});
const {
  modelValue,
  index,
  handlingUnits,
  errorResponse,
  removable,
  showStops,
  requiresConfirmation,
  disabled,
  disableDefaultReferenceNumbers,
  showSaveProductSwitch,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'done'): void;
  (event: 'remove', lineItem: LineItem): void;
  (event: 'update:model-value', newVal: LineItem): void;
}>();

const lineItem: Ref<LineItem> = ref(modelValue.value);

const { t } = useI18n();
const { suggestedFreightClass } = useLineItemMeasurements(lineItem);
const { owningClient } = useParentEntityStore();
const {
  getDefaultReferenceNumbers,
  lineItemReferenceNumberTypes,
  loadReferenceNumberTypes,
} = useReferenceNumberTypes();
const { uid } = useUid('line-item-edit-grid');
useUnsavedChangesConfirmation({
  isDirtyCallback: async () => requiresConfirmation.value && v$.value.$anyDirty,
});

const showButtons = toRef(() => requiresConfirmation.value);

const parentScope = inject<string | undefined>('scope');
const scope = showButtons.value ? uid : parentScope;
const isHazardous = toRef(() => lineItem.value.hazmatData.hazmat);
const hazmatLabel = t('lineItems.hazmat');

const needsRefrigeration = toRef(
  () => lineItem.value.temperatureSettings.needsRefrigeration,
);
const refrigeratedLabel = t('temperatureSettings.refrigerated');

// These rules are shared between both types of line items
const rules = computed(() => {
  const r: Record<string, any> = {
    description: {
      required: required(t('common.description')),
    },
    quantity: {
      required: required(t('lineItems.handling')),
      minValue: minValue(1, t('lineItems.handling')),
    },
    handlingUnit: {
      required: required(
        t('common.paramUom', { param: t('lineItems.handling') }),
      ),
    },
    packagingQty: {
      minValue: minValue(0, t('lineItems.packaging')),
    },
    packagingUnit: {
      required: requiredIf(
        () => lineItem.value.packagingQty,
        t('common.paramUom', { param: t('lineItems.packaging') }),
      ),
    },
    weight: {
      required: required(t('lineItems.weight')),
      minValue: minValue(1, t('lineItems.weight')),
    },
    weightUom: {
      required: required(
        t('common.paramUom', { param: t('lineItems.weight') }),
      ),
    },
    lengthDim: {
      minValue: minValue(1, t('dimensions.length')),
    },
    widthDim: {
      minValue: minValue(1, t('dimensions.width')),
    },
    heightDim: {
      minValue: minValue(1, t('dimensions.height')),
    },
    dimensionalUom: {
      required: required(t('lineItems.unitOfMeasure')),
    },
    freightClass: {},
    productNumber: {},
    linear: {
      matches: matches(/^[0-9]*$/, t('common.units.linearFeet')),
    },
    nmfcNumber: {
      matches: matches(/^\d{5,6}(-\d{1,2})?$/, t('lineItems.nmfcNumber')),
    },
    hazmatData: {
      unNumber: {
        matches: matches(/^(UN|NA)[0-9]{4}$/, t('lineItems.unOrNaNumber')),
      },
      hazardDescription: {},
      hazardClass: {},
      packingGroup: {},
    },
    temperatureSettings: {
      needsRefrigeration: {},
      minTemp: {
        maxValue: maxValue(
          lineItem.value.temperatureSettings.maxTemp,
          t('temperatureSettings.minTemp'),
        ),
      },
      maxTemp: {
        minValue: minValue(
          lineItem.value.temperatureSettings.minTemp,
          t('temperatureSettings.maxTemp'),
        ),
      },
      temperatureUom: {},
    },
  };

  if (isHazardous.value) {
    r.hazmatData.unNumber.required = required(hazmatLabel);
    r.hazmatData.hazardDescription.required = required(hazmatLabel);
    r.hazmatData.hazardClass.required = required(hazmatLabel);
    r.hazmatData.packingGroup.required = required(hazmatLabel);
  }

  if (needsRefrigeration.value) {
    r.temperatureSettings.minTemp.required = required(refrigeratedLabel);
    r.temperatureSettings.maxTemp.required = required(refrigeratedLabel);
    r.temperatureSettings.temperatureUom.required = required(refrigeratedLabel);
  }

  return r;
});
const { setServerErrors, validate, v$ } = useValidations(
  lineItem,
  rules,
  scope,
);

const availableDimsUoms = computed(() => {
  return DIMS_UNITS.filter(
    (uom) => uom.value !== DimsUom.METER && uom.value !== DimsUom.YARD,
  );
});

const showSuggestedFreightClass = computed(() => {
  return (
    suggestedFreightClass.value &&
    suggestedFreightClass.value !== lineItem.value.freightClass
  );
});

const temperatureUomOptions = computed(() => {
  return TEMPERATURE_UNITS.map((tempUom) => ({
    label: t(tempUom.i18nKey),
    value: tempUom.value,
  }));
});

const temperatureSuffix = computed(() => {
  const tempUom = lineItem.value.temperatureSettings.temperatureUom;
  return tempUom ? t(`uoms.temperature.abbreviations.${tempUom}`) : '';
});

const onUseSuggestedFreightClassClicked = () => {
  lineItem.value.freightClass = suggestedFreightClass.value;
};

const onUpdateUnNumber = (newVal: string) => {
  lineItem.value.hazmatData.unNumber = newVal.toUpperCase();
};

const onCancel = async () => {
  // Don't let a user <ESC> if this is a new line item and its invalid
  if (lineItem.value.id || (await validate())) {
    emit('cancel');
  }
};

const onSubmit = async () => {
  if (await validate()) {
    emit('update:model-value', lineItem.value);
    emit('done');
  }
};

defineExpose({
  validate,
});

watch(
  () => errorResponse?.value,
  (newVal: Optional<ErrorResponse>) => {
    if (newVal) {
      setServerErrors(newVal);
    } else if (requiresConfirmation.value) {
      // if `requiresConfirmation` is true, this component is handling validation
      // itself and can't rely on parents to clear external results
      v$.value.$clearExternalResults();
    }
  },
  { deep: true, immediate: true },
);

watch(
  modelValue,
  (newVal: LineItem) => {
    const Clazz = LineItem.getSubclass(newVal);
    lineItem.value = new Clazz(newVal);
    nextTick(() => v$.value.$reset());
  },
  { immediate: true, deep: true },
);

watch(
  lineItem,
  (newVal: LineItem) => {
    if (!requiresConfirmation.value && !isEqual(newVal, modelValue.value)) {
      emit('update:model-value', newVal);
    }
  },
  { deep: true },
);

watch(
  () => owningClient.value?.id,
  (newVal: number | undefined) => {
    if (!disabled.value && newVal) {
      loadReferenceNumberTypes({ level: ReferenceNumberTypeLevel.LINE_ITEM });
    }
  },
  { deep: true, immediate: true },
);

watch(needsRefrigeration, (newVal: boolean) => {
  if (!newVal) {
    lineItem.value.temperatureSettings.minTemp = undefined;
    lineItem.value.temperatureSettings.maxTemp = undefined;
    lineItem.value.temperatureSettings.temperatureUom = undefined;
  }
});

onMounted(async () => {
  if (!disabled.value && owningClient.value?.id) {
    await loadReferenceNumberTypes({
      level: ReferenceNumberTypeLevel.LINE_ITEM,
    });
  }

  // Don't try to set default refNums for existing line items or line items
  // that already have reference numbers
  if (
    !lineItem.value.id &&
    !disableDefaultReferenceNumbers.value &&
    !lineItem.value.referenceNumbers.length
  ) {
    const defaultReferenceNumbers = getDefaultReferenceNumbers(
      lineItemReferenceNumberTypes.value.data,
    );
    if (defaultReferenceNumbers.length) {
      lineItem.value.referenceNumbers = defaultReferenceNumbers;
    }
  }
});
</script>

<style>
.line-item-edit-grid {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);

  .row {
    display: flex;
    gap: var(--sp-3);
    align-items: start;

    &.border-top {
      border-top: var(--divider);
      padding-top: var(--sp-3);
    }
  }

  .dims-input-group.p-inputgroup {
    height: initial;
  }

  .suggestion-button {
    align-self: start;
    margin-top: var(--sp-2);

    .p-button-label {
      width: 100%;
      text-decoration: none !important;
    }
  }
}
</style>
